import { DeploymentStatus } from './status.jsx'
import { formatAge, formatInstant } from '../format.js'
import { LifetimeControl } from './lifetime.jsx'
import { OutsideNotice } from './outside.jsx'
import type { DeploymentDetail } from '../api.js'
import type { JSX } from 'react'

/**
 * The two questions people arrive at an instance with (IM-107).
 *
 * *Is it up*, and *what is its URL*. The screen used to open with a four-row
 * definition list and put the URL behind a button in a panel below it — so the
 * common case, an engineer checking a customer's instance, was the slow one.
 *
 * ## Why nothing is fetched here
 *
 * The endpoints are already on the record when a run has printed them:
 * `DeploymentDetail` carries the outputs of the newest run that recorded any,
 * and IM-40 put them there precisely so this did not have to be a cross-account
 * call on page load. The `Endpoints` panel below still offers the re-read; this
 * only stops the answer being hidden when it is already in hand.
 *
 * ## Why a destroyed instance shows no link
 *
 * The outputs live on the run that printed them, so a destroyed deployment still
 * carries the URL its last apply produced — and that address either answers
 * nothing or, worse, answers as something else now. `deployment.ts` already
 * treats **a failed destroy as not destroyed**, so this reads `destroyed` as the
 * one status where the recorded URL is known not to be this instance's, and says
 * what happened instead of offering a link into a customer's estate that is not
 * theirs any more.
 */

/** The output the whole screen is really about. */
const ENVIRONMENT_URL = 'environmentUrl'
const ADMIN_PORTAL_URL = 'adminPortalUrl'

export const InstanceHead = ({
  deployment,
  baseUrl,
  tenantId,
  role,
  onChanged,
}: {
  readonly deployment: DeploymentDetail
  /** For the lifetime control, which writes — see `lifetime.tsx`. */
  readonly baseUrl: string
  readonly tenantId: string
  readonly role: string
  readonly onChanged: () => void
}): JSX.Element => {
  const url = deployment.outputs?.values[ENVIRONMENT_URL]
  const portal = deployment.outputs?.values[ADMIN_PORTAL_URL]
  const gone = deployment.status === 'destroyed'

  return (
    <section className="panel">
      <div className="run-head">
        <h2>{deployment.deploymentId}</h2>
        <DeploymentStatus status={deployment.status} />
      </div>

      {gone ? (
        <p className="muted">
          This instance was destroyed, so the address its last apply printed is not it any more.
        </p>
      ) : url?.state === 'present' ? (
        <p className="instance-url">
          {/* rel, because this points into a customer's own estate. */}
          <a href={url.value} target="_blank" rel="noreferrer noopener">
            {url.value}
          </a>
          {portal?.state === 'present' ? (
            <a className="linkish" href={portal.value} target="_blank" rel="noreferrer noopener">
              Admin portal
            </a>
          ) : null}
        </p>
      ) : url?.state === 'withheld' ? (
        // Not expected — the field is not sensitive — but the redactor matches
        // shape, so a URL carrying something secret-looking can be masked. Said
        // plainly rather than reported as absent, which would send someone to
        // run `output` again for a value that would be masked again.
        <p className="muted">
          The environment URL was masked in the customer&rsquo;s account, so it was never copied
          here.
        </p>
      ) : url?.state === 'missing' ? (
        <p className="muted">The installer reported no environment URL for this deployment.</p>
      ) : (
        // No outputs at all: nobody has read a log yet, or no run has printed a
        // banner. The panel below is where that is fixed, and it is named rather
        // than left to be found.
        <p className="muted">
          No endpoints have been read for this instance yet &mdash; see Endpoints below.
        </p>
      )}

      {/* Said above the facts, because it changes what they are worth: a
        version and a URL belong to an instance the scan cannot find (IM-156). */}
      <OutsideNotice
        deployment={deployment}
        baseUrl={baseUrl}
        tenantId={tenantId}
        role={role}
        onChanged={onChanged}
      />

      <dl className="detail">
        <dt>Deployed version</dt>
        {/* The newest apply that *succeeded*. A version that was asked for and
          failed leaves the previous one running, and reporting the newer one
          would tell someone they are upgraded when they are not. */}
        <dd>{deployment.chartVersion ?? 'Nothing applied yet'}</dd>
        {/*
          How long this instance has been live (IM-129), and no verdict on it.

          "According to this control plane's records" is said rather than left
          to be assumed: an instance adopted under IM-68 was alive before its
          first run here, and the account screen's scan reads the cluster's own
          creation time, which is the better answer where there is one. A figure
          that silently means two different things in two places is one nobody
          can act on.

          A dash where nothing is live — `never-applied` and `destroyed`. A zero
          would read as brand new and a blank row as a rendering fault.
        */}
        <dt>Live for</dt>
        <dd>
          {deployment.liveSince === undefined ? (
            '—'
          ) : (
            <>
              <time dateTime={deployment.liveSince}>{formatAge(deployment.liveSince)}</time>{' '}
              <span className="muted">
                since {formatInstant(deployment.liveSince)}, by this control plane&rsquo;s records
              </span>
            </>
          )}
        </dd>
        {/*
          What this was meant for, beside how long it has actually been here
          (IM-134). The pair is the whole point: an age is only interesting
          against something somebody said.
        */}
        <LifetimeControl
          baseUrl={baseUrl}
          tenantId={tenantId}
          accountKey={deployment.accountKey}
          deploymentId={deployment.deploymentId}
          {...(deployment.lifetime === undefined ? {} : { lifetime: deployment.lifetime })}
          role={role}
          onChanged={onChanged}
        />
        <dt>Region</dt>
        <dd>{deployment.region ?? 'Not recorded'}</dd>
        <dt>Cloud account</dt>
        <dd className="mono-cell">{deployment.accountKey}</dd>
      </dl>
    </section>
  )
}
